import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";

import ConstList from "../utils/ConstList";
import { LoginUserAtom } from "../stores/LoginUserAtom";

import Loading from "../component/Loading";
import MaintenanceGate from "../component/MaintenanceGate";
import PrivateRouter from "../component/PrivateRouter";
import Setting_User from "../component/Setting_User";

import Login from "./Login";
import Header from "./Header";
import AdminHome from "./Home_By_Admin";
import ReleaceNote from "./ReleaceNote";
import Setting from "./Setting";
import MyPage from "./MyPage";

// 新しい親ページ
import DashboardPage from "./Dashboard";
import CustomersPage from "./Customers";
import SalesReportsPage from "./SalesReports";
import ProjectsPage from "./Projects";

const EMPTY_LOGIN_USER = {
  userId: "",
  email: "",
  userName: "",
  accessToken: "",
  algorithm: "",
};

const Routers = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
  } = useAuth0();

  const setLoginUserData = useSetRecoilState(LoginUserAtom);

  /**
   * 現在、Recoilへの保存まで完了しているAuth0ユーザーID。
   *
   * Auth0上ではログイン済みでも、
   * アクセストークンとユーザー情報の保存が終わるまでは
   * 通常画面を表示しない。
   */
  const [initializedUserId, setInitializedUserId] =
    useState<string | null>(null);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    /**
     * 未ログイン時はRecoilの情報を初期化する。
     */
    if (!isAuthenticated || !user) {
      setLoginUserData(EMPTY_LOGIN_USER);
      setInitializedUserId(null);
      return;
    }

    let cancelled = false;

    const initializeUser = async () => {
      const currentUserId = user.sub ?? "";

      try {
        const accessToken = await getAccessTokenSilently({
          audience: `https://${ConstList.DOMAIN}/api/v2/`,
        });

        if (cancelled) {
          return;
        }

        setLoginUserData({
          userId: currentUserId,
          email: user.email ?? "",
          userName: user.name ?? "",
          accessToken,
          algorithm: ConstList.ALGORITHM,
        });
      } catch (caughtError) {
        console.error(
          "ログインユーザー情報の取得に失敗しました。",
          caughtError,
        );

        if (cancelled) {
          return;
        }

        setLoginUserData(EMPTY_LOGIN_USER);
      } finally {
        if (!cancelled) {
          setInitializedUserId(currentUserId);
        }
      }
    };

    void initializeUser();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isLoading,
    user?.sub,
    user?.email,
    user?.name,
    getAccessTokenSilently,
    setLoginUserData,
  ]);

  const currentAuth0UserId = user?.sub ?? "";

  /**
   * Auth0確認中、またはログインユーザー情報の保存前は、
   * 画面を一切表示せずLoadingだけにする。
   */
  const initializing =
    isLoading ||
    (isAuthenticated &&
      initializedUserId !== currentAuth0UserId);

  return (
    <Router basename={ConstList.SYS_TOPDOMAIN}>
      {initializing ? (
        <Loading />
      ) : isAuthenticated ? (
        <MaintenanceGate>
          <Header />

          <Routes>
            {/* 初期表示 */}
            <Route
              path="/"
              element={<Navigate replace to="/home" />}
            />

            {/* ホーム */}
            <Route
              path="/home"
              element={
                <PrivateRouter>
                  <AdminHome />
                </PrivateRouter>
              }
            />

            {/* =================================================
                新しい主要業務ルート
            ================================================= */}

            {/* 営業ダッシュボード
                tab:
                - all
                - prospective
                - existing
            */}
            <Route
              path="/dashboard"
              element={
                <PrivateRouter>
                  <DashboardPage />
                </PrivateRouter>
              }
            />

            {/* 顧客一覧
                tab:
                - all
                - prospective
                - existing
                - stopped
            */}
            <Route
              path="/customers"
              element={
                <PrivateRouter>
                  <CustomersPage />
                </PrivateRouter>
              }
            />

            {/* 日報
                view:
                - list
                - create

                customerType:
                - prospective
                - existing
            */}
            <Route
              path="/sales-reports"
              element={
                <PrivateRouter>
                  <SalesReportsPage />
                </PrivateRouter>
              }
            />

            {/* 案件管理
                tab:
                - all
                - prospective
                - existing
            */}
            <Route
              path="/projects"
              element={
                <PrivateRouter>
                  <ProjectsPage />
                </PrivateRouter>
              }
            />

            {/* =================================================
                設定・個人・その他
            ================================================= */}

            <Route
              path="/setting"
              element={
                <PrivateRouter>
                  <Setting />
                </PrivateRouter>
              }
            />

            <Route
              path="/setting/users"
              element={
                <PrivateRouter>
                  <Setting_User />
                </PrivateRouter>
              }
            />

            <Route
              path="/my_page"
              element={
                <PrivateRouter>
                  <MyPage />
                </PrivateRouter>
              }
            />

            <Route
              path="/ReleaceNote"
              element={
                <PrivateRouter>
                  <ReleaceNote />
                </PrivateRouter>
              }
            />

            {/* =================================================
                旧URLとの互換性

                既存リンク・ブックマークを壊さないため、
                新しい親ページ＋対象タブへ転送する。
            ================================================= */}

            <Route
              path="/Prospective_customers"
              element={
                <Navigate
                  replace
                  to="/customers?tab=prospective"
                />
              }
            />

            <Route
              path="/ExistingCustomers"
              element={
                <Navigate
                  replace
                  to="/customers?tab=existing"
                />
              }
            />

            <Route
              path="/SDMS_stoppedCustomers"
              element={
                <Navigate
                  replace
                  to="/customers?tab=stopped"
                />
              }
            />

            <Route
              path="/SDMS_caseManagement"
              element={
                <Navigate
                  replace
                  to="/projects?tab=all"
                />
              }
            />

            {/* 存在しないURLはホームへ戻す */}
            <Route
              path="*"
              element={<Navigate replace to="/home" />}
            />
          </Routes>
        </MaintenanceGate>
      ) : (
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
      )}
    </Router>
  );
};

export default Routers;